import { useEffect, useState } from 'react';
import { ValidationResponse } from './ValidationResponse';
import React from 'react';

export interface props {
  placeholder: string;
  mandatory?: boolean;
  title: string;
  initialValue?: string;
  onSelect?: (index: number, value: string) => ValidationResponse | void;
  elementsForDisplay: string[];
  classProps?: string;
  readonly?: boolean;
}

export const Dropdown = (props: props) => {
  const [inputIsFocused, setInputIsFocused] = useState(false);
  const [selectedElement, setSelectedElement] = useState<string | undefined>(undefined);
  const [selectedElementIndex, setSelectedElementIndex] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState('');

  React.useMemo(() => {
    const elements = props.elementsForDisplay ?? [];

    const defaultValue = props.initialValue;
    let priorIndex: number | undefined = undefined;
    let priorValue: string | undefined = undefined;
    if (defaultValue != undefined) {
      const index = elements.indexOf(defaultValue);

      if (index != -1) {
        priorValue = defaultValue;
        priorIndex = index;
      }
    }
    setSelectedElement(priorValue ?? props.placeholder);
    setSelectedElementIndex(priorIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValue]);

  const validationEntry = () => {
    const selectedIndex = selectedElementIndex;
    if (props.onSelect != undefined && selectedIndex != undefined) {
      const validationResponse = props.onSelect(selectedIndex, props.elementsForDisplay[selectedIndex]);
      if (validationResponse) {
        if (validationResponse.isValid) {
          setErrorMessage('');
        } else {
          if (validationResponse.errorMessage != undefined) {
            setErrorMessage(validationResponse.errorMessage);
          } else {
            setErrorMessage('An unknown error occurred');
          }
        }
      }
    }
  };

  useEffect(() => {
    validationEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedElementIndex]);

  const onElementSelect = (index: number) => {
    if (index <= props.elementsForDisplay.length) {
      setSelectedElement(props.elementsForDisplay[index]);
      setSelectedElementIndex(index);
    }
    setInputIsFocused(false);
  };

  const onInputClick = () => {
    if (props.readonly) return;
    setInputIsFocused(!inputIsFocused);
  };

  const errorExists = () => errorMessage != undefined && errorMessage != '';
  const errorControl = () =>
    errorExists() ? <label className="text-feedback-red text-xs">{errorMessage}</label> : <br />;

  const labelTextColour = props.readonly ? 'text-solid-white' : 'text-solid-bold-green';
  const elementsClass = inputIsFocused ? 'block max-h-full overflow-y-scroll' : 'hidden';
  const inputFieldTextColour = selectedElement == props.placeholder ? 'text-secondary-deep-blue-30' : '';
  const backgroundColour = props.readonly ? 'bg-transparent' : 'bg-interactive-bold-green-db-12';

  const elementControl = () => {
    return (
      <div
        className={`${elementsClass} z-10 absolute ${backgroundColour} min-w-[30em] w-fit border-solid-bold-green border-[1px]`}
      >
        {props.elementsForDisplay.map((element: string, index: number) => {
          return (
            <div
              key={index}
              className={`${backgroundColour} hover:bg-interactive-light-teal-100`}
              onClick={() => {
                onElementSelect(index);
              }}
            >
              <div className="pl-3 pt-3 pb-3">
                <span>{element}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const borderColour = props.readonly ? 'border-transparent' : 'border-b-solid-bold-green';

  return (
    <div className={`w-full ${props.classProps ?? ''}`}>
      <div
        className={`${backgroundColour} ${borderColour} border-b-2 w-full cursor-pointer pt-1`}
        onClick={onInputClick}
      >
        <label className={`ml-3 text-xs cursor-pointer ${labelTextColour}`}>
          <span>{props.mandatory == true ? '*' : ''}</span>
          {props.title}
        </label>
        <input
          type="text"
          readOnly
          className={`${inputFieldTextColour} cursor-pointer w-full pl-3 pr-5 pb-1 ${backgroundColour} focus:outline-none bg-origin-content bg-no-repeat bg-[length:12px_7px] bg-[right_bottom_20px]`}
          style={{ backgroundImage: 'url(/dropdownArrow.png)' }}
          value={selectedElement}
          data-placeholder={props.placeholder}
        />
        {elementControl()}
      </div>
      <div>{errorControl()}</div>
    </div>
  );
};
